import { FormControl, InputBase, InputBaseProps, InputLabel, type TextFieldProps } from '@mui/material';
import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ComponentProps,
  type ReactNode,
  type ForwardedRef,
} from 'react';
import * as stylex from '@stylexjs/stylex';
import { Input as FplInput, Label as FplLabel } from '@figma/fpl-components';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { styled, type Theme, type SxProps } from '@/UI/theme.exports';
import { Typography, typographyStyles } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { mergeRefs } from '@/utils/refs';
import { extractSxMinWidth, extractSxWidth, toCssWidth, toStringValue } from './utils';

export type { InputBaseProps, TextFieldProps };
export { LineInput } from './LineInput';

export interface InputProps extends Omit<InputBaseProps, 'size' | 'ref'> {
  variant?: 'dark' | 'light';
  label?: ReactNode;
  helperText?: string;
  size?: 'large' | 'small';
  joinedRight?: boolean;
  /** RAF-deferred focus for modal/dialog inputs that mount asynchronously. */
  autoFocusAsync?: boolean;
  noBlurOnEnter?: boolean;
  noAutoSelect?: boolean;
  noBg?: boolean;
  sx?: SxProps<Theme>;
}

type StyledInputLabelProps = Omit<ComponentProps<typeof InputLabel>, 'size'> & {
  size?: 'large' | 'small';
};

const StyledInput = styled(
  InputBase as (props: InputProps & { ref: ForwardedRef<HTMLInputElement> }) => React.JSX.Element,
  {
    shouldForwardProp: (prop) => prop !== 'size' && prop !== 'noBg',
  },
)(({ theme, size = 'large', noBg = false }: { theme: Theme; size?: 'large' | 'small'; noBg?: boolean }) => ({
  'label + &': {
    marginTop: theme.spacing(2.5),
  },
  '&.MuiInputBase-root': {
    backgroundColor: noBg ? 'transparent' : 'var(--input-background-color)',
    border: '1px solid',
    borderColor: 'var(--input-border-color)',
    borderRadius: 4,
    color: 'var(--input-color)',
    fontFamily: ['"DM Sans"', 'sans-serif'].join(','),
    fontSize: '0.75rem',
    gap: theme.spacing(1),
    minWidth: '20px',
    padding: size === 'large' ? theme.spacing(0.875, 1.5) : theme.spacing(0.5, 1),
    position: 'relative',
    transition: theme.transitions.create(['border-color', 'background-color']),
    '&.MuiInputBase-adornedStart > svg:first-of-type': {
      height: '16px',
      width: '16px',
    },
    '&.MuiInputBase-adornedEnd > svg:last-of-type': {
      height: '16px',
      width: '16px',
    },
    '&.Mui-focused': {
      borderColor: 'var(--input-focus-border-color)',
    },
    '&.Mui-error': {
      backgroundColor: 'var(--input-error-background-color)',
      borderColor: 'var(--input-error-border-color)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'var(--input-disabled-background-color)',
      color: 'var(--input-disabled-color)',
    },
    '& > .MuiInputBase-input': {
      backgroundColor: 'unset',
      height: '16px',
      padding: '0',
      WebkitTextFillColor: 'unset',
      '&::placeholder': {
        color: 'var(--input-placeholder-color)',
        fontSize: '0.75rem',
        fontWeight: 400,
        opacity: 1,
      },
    },
  },
}));

export const StyledInputLabel = styled(InputLabel as (props: StyledInputLabelProps) => React.JSX.Element, {
  shouldForwardProp: (prop) => prop !== 'size',
})(({ size = 'large' }: { size?: 'large' | 'small' }) => ({
  color: size === 'large' ? 'var(--input-label-color)' : 'var(--input-small-label-color)',
  fontFamily: size === 'large' ? '"DM Mono"' : '"DM Sans"',
  fontSize: '0.75rem',
  fontWeight: 400,
  transform: 'translateY(-4px)',
  '&.Mui-focused': {
    color: size === 'large' ? 'var(--input-label-color)' : 'var(--input-small-label-color)',
  },
}));

const DarkStyles = {
  '--input-background-color': color.Black92,
  '--input-border-color': color.White08_T,
  '--input-color': token.text,
  '--input-disabled-background-color': color.Black92,
  '--input-disabled-color': color.White16_T,
  '--input-error-background-color': color.Weavy_Error_08_T,
  '--input-error-border-color': color.Weavy_Error_64_T,
  '--input-error-color': color.Weavy_Error,
  '--input-focus-border-color': color.White40_T,
  '--input-label-color': color.White80_T,
  '--input-small-label-color': color.White64_T,
  '--input-placeholder-color': color.White64_T,
};

const LightStyles = {
  '--input-background-color': color.Black08_T,
  '--input-border-color': color.White04_T,
  '--input-color': color.Black100,
  '--input-disabled-background-color': color.Black04_T,
  '--input-disabled-color': color.Black40_T,
  '--input-error-background-color': color.Weavy_Error_08_T,
  '--input-error-border-color': color.Weavy_Error,
  '--input-error-color': color.Weavy_Error,
  '--input-focus-border-color': color.Black40_T,
  '--input-label-color': color.Black92_T,
  '--input-small-label-color': color.Black64_T,
  '--input-placeholder-color': color.Black64_T,
};

/**
 * Legacy MUI implementation. Rendered when the FPL migration flag is off so the
 * flag-off path stays byte-for-byte identical for rollback safety.
 */
const LegacyInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'dark',
      label,
      id,
      helperText,
      size = 'large',
      autoFocusAsync = false,
      noBlurOnEnter = false,
      noAutoSelect = false,
      noBg = false,
      joinedRight = false,
      inputRef: inputRefProp,
      onFocus,
      sx,
      ...props
    },
    ref,
  ) => {
    const inputRefInternal = useRef<HTMLInputElement | null>(null);
    const mergedInputRef = useMemo(() => mergeRefs<HTMLInputElement>(inputRefInternal, inputRefProp), [inputRefProp]);

    useEffect(() => {
      if (!autoFocusAsync) return;
      const rafId = requestAnimationFrame(() => {
        inputRefInternal.current?.focus();
      });
      return () => cancelAnimationFrame(rafId);
    }, [autoFocusAsync]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!noBlurOnEnter && event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        inputRefInternal.current?.blur();
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (!noAutoSelect) {
        event.target.select();
      }
      onFocus?.(event);
    };

    return (
      <FormControl
        variant="standard"
        sx={{
          ...(variant === 'dark' ? DarkStyles : LightStyles),
          ...(joinedRight && {
            marginLeft: '-1px',
            '& .MuiInputBase-root': { borderRadius: '0 4px 4px 0' },
          }),
          ...sx,
        }}
        fullWidth={props.fullWidth}
      >
        {label ? (
          <StyledInputLabel size={size} htmlFor={id}>
            {label}
          </StyledInputLabel>
        ) : null}
        <StyledInput
          ref={ref}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          {...props}
          inputRef={mergedInputRef}
          id={id}
          size={size}
          noBg={noBg}
        />
        {helperText ? (
          <Typography
            variant="body-xs-rg"
            id={`${id}-helper-text`}
            color={props.error ? 'var(--input-error-color)' : 'var(--input-color)'}
            sx={{
              alignSelf: 'flex-start',
              marginTop: 0.5,
              marginLeft: 0.5,
            }}
          >
            {helperText}
          </Typography>
        ) : null}
      </FormControl>
    );
  },
);

LegacyInput.displayName = 'LegacyInput';

const styles = stylex.create({
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
  },
  fieldDefaultWidth: {
    width: '100%',
    minWidth: 0,
  },
  adornment: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: token.textSecondary,
    position: 'relative',
  },
  /**
   * FPL gives the bare input `var(--spacer-2)` of edge padding only when it has no sibling on that
   * side inside `Input.Root` (see `fpl/components/src/Input/input.tsx`); with an adornment present
   * it drops to 0 and the adornment becomes the field's edge. The slot carries that spacer so the
   * content inset is identical whether or not an adornment is rendered, plus a smaller gap on the
   * inner side to separate the adornment from the value.
   *
   * Both insets belong here rather than at the call site: an adornment that pads itself as well
   * ends up double-inset, which is what several node fields used to do to work around the missing
   * edge padding.
   */
  adornmentStart: {
    paddingLeft: spacer.spacer2,
    paddingRight: spacer.spacer1,
  },
  adornmentEnd: {
    paddingRight: spacer.spacer2,
    paddingLeft: spacer.spacer1,
  },
  helperText: {
    margin: 0,
    color: token.textSecondary,
  },
  helperTextError: {
    color: token.textDanger,
  },
});

type FplInputType = ComponentProps<typeof FplInput>['type'];

/**
 * FPL `Input.Root` forwards pointerdown to the text field (and pointer-captures the
 * gesture), which swallows clicks on interactive adornments like clear buttons whose
 * pointer target is an inner icon rather than the `<button>` itself. Stopping
 * propagation keeps those adornments clickable and matches legacy MUI, where clicking
 * an adornment did not focus the field.
 */
function stopAdornmentFocusForwarding(event: React.PointerEvent<HTMLSpanElement>) {
  event.stopPropagation();
}

/**
 * FPL-backed implementation (the `WeavInput` adapter). Rendered when the FPL
 * migration flag is on. Maps the legacy MUI-shaped props to FPL `Input`:
 * - MUI event-first `onChange(event)` <- FPL value-first `(value, { event })`.
 * - `size` large -> `lg` (32px), small -> `md` (24px).
 * - `error` -> `aria-invalid`; `label`/`helperText` rendered as siblings.
 * - prefix/suffix adornments composed via `Input.Root`.
 * - a curated subset of MUI `inputProps` (type, min/max/step, length, pattern,
 *   inputMode, and `aria-label`) is forwarded to the FPL input, which accepts native
 *   input attrs. `aria-labelledby` is intentionally not forwarded — FPL disallows it.
 *
 * Legacy-only MUI APIs (`variant`, `noBg`, and all of `sx` except `width`) are
 * intentionally dropped here per ADR-002: they stay on the flag-off path and do
 * not leak into FPL. `sx.width` is the one exception — it is product layout, so
 * it carries over to the owner wrapper (ADR-001).
 */
const FplWeaveInput = forwardRef<HTMLInputElement, InputProps>(function FplWeavInput(
  {
    label,
    id,
    helperText,
    size = 'large',
    error,
    placeholder,
    disabled,
    readOnly,
    required,
    name,
    type,
    autoComplete,
    autoFocus,
    autoFocusAsync = false,
    noBlurOnEnter = false,
    noAutoSelect = false,
    value,
    defaultValue,
    startAdornment,
    endAdornment,
    inputRef: inputRefProp,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyDownCapture,
    onPaste,
    inputProps,
    sx,
    className,
    'aria-label': ariaLabel,
  },
  ref,
) {
  const inputRefInternal = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMemo(
    () => mergeRefs<HTMLInputElement>(inputRefInternal, inputRefProp, ref),
    [inputRefProp, ref],
  );

  useEffect(() => {
    if (!autoFocusAsync) return;
    const rafId = requestAnimationFrame(() => {
      inputRefInternal.current?.focus();
    });
    return () => cancelAnimationFrame(rafId);
  }, [autoFocusAsync]);

  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = helperText ? `${inputId}-helper-text` : undefined;
  const fplSize = size === 'small' ? 'md' : 'lg';
  const stringValue = toStringValue(value);
  const stringDefaultValue = toStringValue(defaultValue);
  const sxWidth = toCssWidth(extractSxWidth(sx));
  const sxMinWidth = toCssWidth(extractSxMinWidth(sx)) ?? sxWidth;

  // MUI callers pass native `<input>` attributes through `inputProps`. FPL's `Input`
  // accepts these attributes directly, so forward a curated, type-safe subset
  // (type, numeric constraints, length, pattern, input mode). `inputProps.type`
  // takes precedence over the top-level `type`, matching MUI (it set both on the
  // native input, with `inputProps` winning). `aria-labelledby` is intentionally not
  // forwarded — FPL's input disallows it and labels via `id`/`aria-label` instead.
  const nativeInputProps = inputProps as React.InputHTMLAttributes<HTMLInputElement> | undefined;
  const resolvedType = (nativeInputProps?.type ?? type) as FplInputType;
  // FPL forbids `aria-labelledby`, so callers migrating off a (often dead) `labelledby`
  // idref pass real label text instead. Accept it from either the top-level `aria-label`
  // or MUI's idiomatic `inputProps['aria-label']`, preferring the top-level prop.
  const resolvedAriaLabel = nativeInputProps?.['aria-label'] ?? ariaLabel;

  const handleChange = (_value: string, opts: { event: React.ChangeEvent<HTMLInputElement> }) => {
    onChange?.(opts.event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!noAutoSelect) {
      event.target.select();
    }
    onFocus?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!noBlurOnEnter && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      inputRefInternal.current?.blur();
    }
    onKeyDown?.(event);
  };

  // FPL renders `disabled` as a focusable `readOnly` input (an a11y choice), unlike
  // MUI's native `disabled`, which fires no events. Consumers (e.g. `useNumberInput` in
  // SeedNode/NumberInput) assume a disabled field never focuses/changes/blurs; letting
  // those handlers run can submit a value and mutate graph state that's invalid while
  // disabled (e.g. a randomized seed), which panics downstream. So drop every
  // interaction handler when disabled to match MUI's disabled semantics. `onKeyDownCapture`
  // and `onPaste` are forwarded because MUI consumers attach them directly (e.g.
  // `useNumberInput` wires keyboard handling via `onKeyDownCapture`, and the email/model
  // inputs paste-parse via `onPaste`).
  const interactionHandlers = disabled
    ? undefined
    : {
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur,
        onKeyDown: handleKeyDown,
        onKeyDownCapture,
        onPaste,
      };

  const field = (
    <FplInput
      ref={mergedRef}
      id={inputId}
      aria-label={resolvedAriaLabel}
      aria-invalid={error || undefined}
      aria-describedby={helperId}
      size={fplSize}
      name={name}
      type={resolvedType}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      value={stringValue}
      defaultValue={stringDefaultValue}
      min={nativeInputProps?.min}
      max={nativeInputProps?.max}
      step={nativeInputProps?.step}
      maxLength={nativeInputProps?.maxLength}
      minLength={nativeInputProps?.minLength}
      pattern={nativeInputProps?.pattern}
      inputMode={nativeInputProps?.inputMode}
      {...interactionHandlers}
    />
  );

  const control =
    startAdornment || endAdornment ? (
      <FplInput.Root size={fplSize}>
        {startAdornment ? (
          <span {...stylex.props(styles.adornment, styles.adornmentStart)} onPointerDown={stopAdornmentFocusForwarding}>
            {startAdornment}
          </span>
        ) : null}
        {field}
        {endAdornment ? (
          <span {...stylex.props(styles.adornment, styles.adornmentEnd)} onPointerDown={stopAdornmentFocusForwarding}>
            {endAdornment}
          </span>
        ) : null}
      </FplInput.Root>
    ) : (
      field
    );

  const hasExplicitWidth = Boolean(sxWidth);
  const fieldProps = stylex.props(styles.field, !hasExplicitWidth && styles.fieldDefaultWidth);
  const mergedClassName = [fieldProps.className, className].filter(Boolean).join(' ') || undefined;

  return (
    <div
      {...fieldProps}
      className={mergedClassName}
      style={
        sxWidth != null || sxMinWidth != null
          ? {
              ...fieldProps.style,
              ...(sxWidth != null ? { width: sxWidth } : {}),
              ...(sxMinWidth != null ? { minWidth: sxMinWidth } : {}),
            }
          : fieldProps.style
      }
    >
      {label ? <FplLabel htmlFor={inputId}>{label}</FplLabel> : null}
      {control}
      {helperText ? (
        <p
          id={helperId}
          {...stylex.props(typographyStyles.textBodyMedium, styles.helperText, error && styles.helperTextError)}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
});

FplWeaveInput.displayName = 'FplWeaveInput';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const isFplEnabled = useFplMigrationFlag();

  if (isFplEnabled) {
    return <FplWeaveInput ref={ref} {...props} />;
  }

  return <LegacyInput ref={ref} {...props} />;
});

Input.displayName = 'Input';
